import { Box, Tab, Tabs } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { PositionRoleEnum, PositionStatusEnum } from "@sinch/core/entities/serverEnums";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { MdsEmojiPeople, MdsGroup } from "@sinch/icons";
import { filter, modify } from "ramda";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { LoadingWrapper } from "../../LoadingWrapper/LoadingWrapper";
import { MdsIcon } from "../../MdsIcon";
import { ShiftHeader } from "../../Shift";
import { ShiftDetail } from "./queries.graphql";
import { PositionShiftPanelDetailFragment, ShiftDetailQuery } from "./queries.types";
import { ShiftBasicInfo } from "./ShiftBasicInfo";
import { ApplicantRow, AttendanceRow, EmptyApplicantRow, EmptyPositionRow } from "./ShiftTable";
import { ShiftAttendanceTable } from "./ShiftTable/ShiftAttendanceTable";
import { ShiftTransportInfo } from "./ShiftTransportInfo";
import { ShiftActionMenu } from "@sinch/components/application/Shift/components/ShiftActionMenu";

interface ShiftDetailPanelProps {
  shiftId: string;
  attendanceConfirmation?: boolean;
}

export enum ShiftDetailPanelTabs {
  BasicInfo,
  Transport,
}

export const ShiftDetailPanel: React.FC<ShiftDetailPanelProps> = ({ shiftId, attendanceConfirmation }) => {
  const { data, refetch } = useQueryHandler<ShiftDetailQuery>(ShiftDetail, { id: shiftId });
  const [tab, setTab] = useState<ShiftDetailPanelTabs>(ShiftDetailPanelTabs.BasicInfo);
  const { t } = useTranslation();

  if (!data?.shift) {
    return <LoadingWrapper on sx={{ height: "100%" }} />;
  }

  const handleChangeTab = (_: React.SyntheticEvent, value: ShiftDetailPanelTabs) => {
    setTab(value);
  };

  const staffCount = data.shift.positions.reduce((result, position) => result + position.attendances.length, 0);
  const applicantsCapacitySize = data.shift.positions.reduce((result, position) => result + position.applicantSize, 0);

  return (
    <Box
      sx={{
        position: "relative",
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{
          padding: 2,
        }}
      >
        <Grid size={12}>
          <Grid container wrap="nowrap">
            <Grid>
              <ShiftHeader shift={data.shift} />
            </Grid>
            <Grid>
              <ShiftActionMenu disableCollapse shift={data.shift} />
            </Grid>
          </Grid>
        </Grid>
        {!!data.shift.transportTrips?.length && (
          <Grid size={12}>
            <Tabs
              onChange={handleChangeTab}
              sx={(theme) => ({
                minHeight: "unset",
                "& .MuiButtonBase-root.MuiTab-root": {
                  minHeight: "unset",
                  padding: theme.spacing(0.5, 1),
                },
              })}
              value={tab}
            >
              <Tab label={t("basicInfo")} value={ShiftDetailPanelTabs.BasicInfo} />
              <Tab label={t("Transport.transport")} value={ShiftDetailPanelTabs.Transport} />
            </Tabs>
          </Grid>
        )}
        <Grid
          size={12}
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          {tab === ShiftDetailPanelTabs.BasicInfo && (
            <ShiftBasicInfo clientReminders={data.clientShiftReminderLogs} shift={data.shift} />
          )}
          {tab === ShiftDetailPanelTabs.Transport && <ShiftTransportInfo transportTrips={data.shift.transportTrips} />}
        </Grid>
      </Grid>
      <ShiftAttendanceTable
        includeCapacity
        label={t("Staff.labelCount", {
          staffCount: staffCount,
        })}
        labelIcon={<MdsIcon icon={MdsGroup} />}
        renderRows={(position, sortRows) => {
          const emptyPositions = position.size - position.attendances.length;
          return (
            <>
              {sortRows(position.attendances).map((attendance) => (
                <AttendanceRow
                  key={attendance.id}
                  attendance={attendance}
                  currentRole={position.role as PositionRoleEnum}
                  onUpdate={refetch}
                  positionStatus={position.status as PositionStatusEnum}
                />
              ))}
              <EmptyPositionRow
                attendanceConfirmation={attendanceConfirmation}
                emptyPositions={emptyPositions}
                isJoinable={!data?.shift?.job.order.quote && position.status !== PositionStatusEnum.Pending}
                onUpdate={refetch}
                position={position}
              />
            </>
          );
        }}
        shift={data.shift}
      />
      {applicantsCapacitySize > 0 && (
        <Box
          sx={{
            pt: 2,
          }}
        >
          <ShiftAttendanceTable
            label={t("Applicant.labelPlural")}
            labelIcon={<MdsIcon icon={MdsEmojiPeople} />}
            renderRows={(position, sortRows) => (
              <>
                {sortRows(position.applicants).map((applicant) => (
                  <ApplicantRow key={applicant.id} applicant={applicant} onUpdate={refetch} />
                ))}
                {position.applicants.length <= 0 && <EmptyApplicantRow />}
              </>
            )}
            shift={modify(
              "positions",
              filter<PositionShiftPanelDetailFragment>(({ applicantSize }) => applicantSize > 0),
              data.shift
            )}
          />
        </Box>
      )}
    </Box>
  );
};
